import { Component } from '@theme/component';

/**
 * Accessible tabs (WAI-ARIA tabs pattern, manual activation by click,
 * automatic activation on arrow keys). Panels stay visible until the
 * element is defined, so content is readable without JavaScript.
 *
 * @typedef {object} TabsRefs
 * @property {HTMLButtonElement[]} tabs - Buttons with role="tab".
 * @property {HTMLElement[]} panels - Elements with role="tabpanel", same order as tabs.
 *
 * @extends {Component<TabsRefs>}
 */
class LumelTabs extends Component {
  requiredRefs = ['tabs', 'panels'];

  connectedCallback() {
    super.connectedCallback();
    const selected = this.refs.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.#select(Math.max(selected, 0), false);
  }

  updatedCallback() {
    super.updatedCallback();
    const selected = this.refs.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.#select(Math.max(selected, 0), false);
  }

  /**
   * @param {MouseEvent} event
   */
  handleClick = (event) => {
    const tab = event.target;
    if (!(tab instanceof HTMLButtonElement)) return;
    const index = this.refs.tabs.indexOf(tab);
    if (index === -1) return;
    this.#select(index, false);
  };

  /**
   * @param {KeyboardEvent} event
   */
  handleKeydown = (event) => {
    const { tabs } = this.refs;
    const current = tabs.findIndex((tab) => tab === document.activeElement);
    if (current === -1) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const next = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const previous = isRtl ? 'ArrowRight' : 'ArrowLeft';

    /** @type {number | null} */
    let target = null;
    if (event.key === next) target = (current + 1) % tabs.length;
    if (event.key === previous) target = (current - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') target = 0;
    if (event.key === 'End') target = tabs.length - 1;
    if (target === null) return;

    event.preventDefault();
    this.#select(target, true);
  };

  /**
   * @param {number} index
   * @param {boolean} focus
   */
  #select(index, focus) {
    const { tabs, panels } = this.refs;

    for (const [i, tab] of tabs.entries()) {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      if (active && focus) tab.focus();
    }

    for (const [i, panel] of panels.entries()) {
      panel.hidden = i !== index;
    }
  }
}

if (!customElements.get('lumel-tabs')) customElements.define('lumel-tabs', LumelTabs);
